import { Link } from 'react-router-dom';
import PageShell, { PageTitle } from '../components/PageShell';
import CallButton from '../components/CallButton';
import HelpBox from '../components/HelpBox';
import ArticleList from '../components/ArticleList';
import FaqAccordion from '../components/FaqAccordion';
import { articlePath, articlesIn } from '../constants/articles';
import { FAQ_GROUPS } from '../constants/content';
import { IMAGES } from '../constants/images';

const FAQS = [
  ...FAQ_GROUPS.find((g) => g.id === 'ftm-2024').items,
  {
    q: 'Can I use FTM 2024 without an Ancestry membership?',
    a: 'Absolutely. The program is fully usable on its own. An Ancestry membership only unlocks subscriber-only records and some of the hints that point to them.',
  },
  {
    q: 'I own a desktop and a laptop. Can both have FTM 2024?',
    a: 'In most cases, yes, as long as both computers are yours. If the second activation is refused, the publisher can reset your activation count.',
  },
  {
    q: 'Do my color codes and photo links survive the upgrade?',
    a: 'They should. The conversion carries across people, facts, sources, colors, and media links. If a few pictures show as missing afterwards, Find Missing Media usually reconnects them in minutes.',
  },
];

const WORKSPACES = [
  ['Plan', 'Your starting point: open or create trees, manage the Ancestry link, and see what you worked on last.'],
  ['People', 'The heart of the program, with pedigree and family views for adding relatives and editing their details.'],
  ['Places', 'A map of where events happened, plus tools to clean up inconsistent place names.'],
  ['Media', 'One home for pictures, scans, audio, and video, each linked to the people it belongs to.'],
  ['Sources', 'Records and citations that back up each fact, so anyone can check your conclusions.'],
  ['Publish', 'Charts, reports, and books ready to print, save as PDF, or share.'],
  ['Web Search', 'Look for records online and line them up with the person you are researching.'],
];

const HIGHLIGHTS = [
  {
    img: IMAGES.features.charts,
    title: 'Beautiful Charts',
    text: 'From simple five-generation pedigrees to wide fan charts for a reunion wall, each chart can be styled and printed.',
  },
  {
    img: IMAGES.features.maps,
    title: 'Migration Maps',
    text: 'Watch your family move across towns, states, and countries, and fix place names that were typed several ways.',
  },
  {
    img: IMAGES.features.hints,
    title: 'Helpful Hints',
    text: 'Suggestions for records that might match your ancestors. Review each one; accept only what fits.',
  },
  {
    img: IMAGES.features.media,
    title: 'Media Library',
    text: 'Keep every scan and photo in one searchable place and tag it to the people and events it shows.',
  },
  {
    img: IMAGES.features.photo,
    title: 'Photo Touch-Ups',
    text: 'Brighten faded prints and tidy up damage while keeping the original file safe.',
  },
  {
    img: IMAGES.features.web,
    title: 'Online Connections',
    text: 'Search records from inside the program and keep an Ancestry tree matched to your desktop tree.',
  },
];

const REQUIREMENTS = [
  ['Operating system', 'Windows 10 or Windows 11', 'A recent version of macOS'],
  ['Processor', 'Current Intel or AMD chip', 'Intel or Apple silicon'],
  ['Memory', 'At least 4 GB; 8 GB or more is smoother', 'At least 4 GB; 8 GB or more is smoother'],
  ['Storage', 'Several GB, plus space for your photos', 'Several GB, plus space for your photos'],
  ['Screen', '1280 × 800 minimum', '1280 × 800 minimum'],
  ['Internet', 'Needed for activation, updates, syncing, and searches', 'Needed for activation, updates, syncing, and searches'],
];

const PROBLEMS = [
  ['Setup will not launch or stops part-way', 'install-family-tree-maker-2024-windows'],
  ['Mac warns that the app cannot be opened', 'install-family-tree-maker-2024-mac'],
  ['Activation fails or the serial is refused', 'activate-family-tree-maker-2024'],
  ['Program crashes, freezes, or will not start', 'ftm-2024-not-opening-fix'],
  ['An old tree will not convert to 2024', 'upgrade-to-family-tree-maker-2024'],
  ['Restoring a backup on a different computer', 'backup-and-restore-ftm-2024'],
];

export default function Ftm2024Page() {
  return (
    <PageShell>
      <PageTitle
        title="Family Tree Maker 2024"
        lead="Everything you need to install, activate, upgrade to, and troubleshoot the current release of Family Tree Maker."
        crumbs={[{ label: 'Family Tree Maker 2024' }]}
      />

      <p>
        FTM 2024 is the edition of Family Tree Maker that the publisher currently sells and updates. If
        you have used the 2017 or 2019 release, the screens will look instantly familiar. The real
        difference is under the hood: it is built to run properly on the latest Windows and macOS
        versions and keeps receiving fixes.
      </p>
      <p>
        On this page you will find a plain-English overview, the system requirements, install and
        upgrade steps, and links to our most useful FTM 2024 guides. If you would rather skip the
        reading, a technician is a phone call or chat message away.
      </p>

      <CallButton />

      <section className="entry-section">
        <h2>FTM 2024 in a Nutshell</h2>
        <p>
          Think of Family Tree Maker as a private research notebook that happens to be very organized.
          You enter people and link them as parents, partners, and children. Each person can carry
          facts, notes, pictures, and the sources that prove them. When you want to share, the program
          turns it all into charts, reports, or a book.
        </p>
        <p>
          Although the tree is stored on your computer, FTM 2024 can reach out to the internet when you
          ask it to: Ancestry for hints, searches, and FamilySync; FamilySearch for comparing against
          the free shared tree; and TreeVault for a cloud copy of your work.
        </p>
      </section>

      <section className="entry-section">
        <h2>Is Upgrading Worth It?</h2>
        <ul>
          <li><strong>You have a new computer:</strong> FTM 2024 is the smoothest way to get up and running on it.</li>
          <li><strong>Your operating system just updated:</strong> older editions can struggle on the newest Windows and macOS releases.</li>
          <li><strong>You want fixes:</strong> the current edition is the one receiving updates from the publisher.</li>
          <li><strong>You rely on syncing:</strong> keeping up to date helps FamilySync and web searches work reliably.</li>
          <li><strong>You do not want to relearn anything:</strong> the layout is the same one you already know.</li>
        </ul>
        <p>
          For a side-by-side look, see{' '}
          <Link to={articlePath('ftm-2024-vs-previous-versions')}>how FTM 2024 compares with earlier editions</Link>.
        </p>
      </section>

      <CallButton />

      <section className="entry-section">
        <h2>Features You Will Use Every Day</h2>
        <div className="img-grid">
          {HIGHLIGHTS.map((item) => (
            <div key={item.title} className="img-card">
              <img src={item.img} alt="" width={640} height={427} loading="lazy" />
              <div className="img-card__body">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="entry-section">
        <h2>A Tour of the Seven Workspaces</h2>
        <div className="table-wrap">
          <table className="data-table data-table--left">
            <thead>
              <tr>
                <th scope="col">Workspace</th>
                <th scope="col">What you do there</th>
              </tr>
            </thead>
            <tbody>
              {WORKSPACES.map(([name, text]) => (
                <tr key={name}>
                  <td><strong>{name}</strong></td>
                  <td>{text}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <CallButton />

      <section className="entry-section">
        <h2>Will It Run on My Computer?</h2>
        <p className="text-center">
          Here is a general guide. The publisher occasionally revises its list, so double-check their
          website before buying. We go into more depth in{' '}
          <Link to={articlePath('system-requirements-ftm-2024')}>our FTM 2024 requirements guide</Link>.
        </p>
        <div className="table-wrap">
          <table className="data-table data-table--left">
            <thead>
              <tr>
                <th scope="col">Component</th>
                <th scope="col">Windows</th>
                <th scope="col">Mac</th>
              </tr>
            </thead>
            <tbody>
              {REQUIREMENTS.map(([item, win, mac]) => (
                <tr key={item}>
                  <td><strong>{item}</strong></td>
                  <td>{win}</td>
                  <td>{mac}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="entry-section">
        <h2>Before You Buy or Install: A Quick Checklist</h2>
        <ul>
          <li>Confirm which operating system version your computer is running.</li>
          <li>Make sure you can sign in as an administrator on the computer.</li>
          <li>Back up any existing tree from your current edition, including media.</li>
          <li>If your tree syncs with Ancestry, run one last sync in the old edition.</li>
          <li>Keep your order email handy; it holds your download link and serial number.</li>
          <li>Free up disk space if your photo collection is large.</li>
        </ul>
      </section>

      <CallButton />

      <section className="entry-section">
        <h2>Installing FTM 2024 Step by Step</h2>
        <h3>Windows</h3>
        <ol>
          <li>Click the download link in your purchase email, or grab the installer from the publisher’s site.</li>
          <li>Open the file. When Windows asks for permission to make changes, choose Yes.</li>
          <li>Agree to the license and leave the installation folder as suggested.</li>
          <li>Launch Family Tree Maker 2024 and enter your serial number to activate.</li>
        </ol>
        <h3>Mac</h3>
        <ol>
          <li>Download the macOS installer.</li>
          <li>Drag the Family Tree Maker icon into the Applications folder.</li>
          <li>Open it from Applications. If macOS warns you, confirm that you want to open it.</li>
          <li>Enter your serial number when prompted.</li>
        </ol>
        <p>
          Need more detail?{' '}
          <Link to={articlePath('install-family-tree-maker-2024-windows')}>Windows guide</Link> ·{' '}
          <Link to={articlePath('install-family-tree-maker-2024-mac')}>Mac guide</Link> ·{' '}
          <Link to={articlePath('activate-family-tree-maker-2024')}>Activation help</Link>
        </p>
      </section>

      <section className="entry-section">
        <h2>Bringing Your Old Tree Across</h2>
        <ol>
          <li>In your current edition, install any pending update and finish syncing.</li>
          <li>Create a backup and tick the option to include media files.</li>
          <li>Install FTM 2024 and activate it.</li>
          <li>Open your old tree in 2024 and allow the conversion to complete.</li>
          <li>Compare the number of people, check a few photos, and confirm sync status.</li>
          <li>Only then think about uninstalling the older version.</li>
        </ol>
        <p>
          Our <Link to={articlePath('upgrade-to-family-tree-maker-2024')}>complete upgrade guide</Link>{' '}
          covers the finer points.
        </p>
      </section>

      <HelpBox
        title="Rather Not Do the Upgrade Alone?"
        text="A technician can stay on the line while you move your trees and photos into FTM 2024, checking each step as you go."
      />

      <section className="entry-section">
        <h2>Fixes for Common FTM 2024 Hiccups</h2>
        <ul className="link-list">
          {PROBLEMS.map(([label, slug]) => (
            <li key={slug}>
              <Link to={articlePath(slug)}>{label}</Link>
            </li>
          ))}
        </ul>
      </section>

      <section className="entry-section">
        <h2>Good Habits for FTM 2024 Users</h2>
        <ul>
          <li>Finish each session with a backup, and keep at least one copy away from your computer.</li>
          <li>Run Compact File once a month if your tree has thousands of people.</li>
          <li>Keep all media in a single folder and rename files only from inside the program.</li>
          <li>Wait for syncs to finish before you close the program or shut down.</li>
          <li>Give each family line a color so it is easy to follow in big trees.</li>
          <li>Cite sources the moment you add a fact; future you will be grateful.</li>
        </ul>
      </section>

      <CallButton />

      <section className="entry-section">
        <h2>Our FTM 2024 Guides</h2>
        <ArticleList articles={articlesIn('ftm-2024')} />
      </section>

      <section className="entry-section">
        <h2>FTM 2024 Questions and Answers</h2>
        <FaqAccordion items={FAQS} />
      </section>

      <CallButton />
    </PageShell>
  );
}
